import React, { useEffect, useState } from 'react';
import { UgcAdProject, UgcAdScene } from '../../services/api/ugcAdService';
import { TaskyButton, TaskyCard, TaskyEmptyState, TaskyAlert } from '../tasky';
import { Loader2, Download, Combine, CheckCircle2, Film, FileText, ImageIcon, Wand2, RotateCcw } from 'lucide-react';

/**
 * ステップ⑥: 書き出し（合成。song-ad ExportStep 踏襲）。
 *
 * クリップ（動画化シーン）＋静止画シーン＋テロップ＋CTAカードを合成する。
 * 全シーンのストーリーボードが揃うまでは合成できない。
 * 合成中は段階メッセージをクライアント側タイマーで演出する（backendのcompose jobには
 * 細かい進捗が無いため。song-ad FB12 と同じ見た目だけの演出）。
 */

interface ExportStepProps {
  project: UgcAdProject;
  scenes: UgcAdScene[];
  isRequesting: boolean;
  /** `allowStill` は「クリップ無しの静止画版で書き出す」を明示的に選んだときだけ true */
  onCompose: (allowStill?: boolean) => Promise<void>;
  onGoToScript: () => void;
  onGoToStoryboard: () => void;
  onGoToMotion: () => void;
}

export const COMPOSE_STAGE_MESSAGES = [
  'シーンとクリップを確認しています',
  'テロップを配置しています',
  'ブランドロゴとCTAを配置しています',
  'シーンをつなぎ合わせています',
  '完成動画を組み立てています',
];

const COMPOSE_STAGE_INTERVAL_MS = 2600;

/** 合成中は段階メッセージを一定間隔で進める。実際の完了判定はproject.statusのポーリングに委ねる（演出のみ） */
export function useComposeStageMessage(active: boolean): { message: string; percent: number } {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setIndex((i) => Math.min(COMPOSE_STAGE_MESSAGES.length - 1, i + 1));
    }, COMPOSE_STAGE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [active]);

  const percent = Math.round(((index + 1) / COMPOSE_STAGE_MESSAGES.length) * 92);
  return { message: COMPOSE_STAGE_MESSAGES[index], percent };
}

const ExportStep: React.FC<ExportStepProps> = ({
  project,
  scenes,
  isRequesting,
  onCompose,
  onGoToScript,
  onGoToStoryboard,
  onGoToMotion,
}) => {
  const isComposing = project.status === 'composing';
  const { message: stageMessage, percent: stagePercent } = useComposeStageMessage(isComposing);
  /** 「静止画版で書き出す」は誤操作しないよう明示のチェックを経由させる */
  const [stillConfirmed, setStillConfirmed] = useState(false);

  const processNav = (
    <div className="flex flex-wrap items-center gap-1.5 mb-5">
      <span className="text-xs text-gray-400 mr-1">工程をやり直す:</span>
      <TaskyButton variant="outline" size="sm" onClick={onGoToScript}>
        <FileText className="w-3.5 h-3.5" />
        構成案
      </TaskyButton>
      <TaskyButton variant="outline" size="sm" onClick={onGoToStoryboard}>
        <ImageIcon className="w-3.5 h-3.5" />
        ストーリーボード
      </TaskyButton>
      <TaskyButton variant="outline" size="sm" onClick={onGoToMotion}>
        <Wand2 className="w-3.5 h-3.5" />
        モーション
      </TaskyButton>
    </div>
  );

  if (scenes.length === 0) {
    return (
      <div>
        {processNav}
        <TaskyEmptyState
          icon={<Film />}
          title="先に構成案を作成してください"
          action={
            <TaskyButton onClick={onGoToScript}>
              <FileText className="w-4 h-4" />
              構成案へ
            </TaskyButton>
          }
        />
      </div>
    );
  }

  const composeFailed = project.status === 'compose_failed';
  /** 「動画化」指定なのにクリップ未生成のシーン */
  const pendingClips = scenes.filter((s) => s.motion_mode === 'clip' && !s.clip.video);

  /**
   * サーバーが判定した「足りない必須素材」（UGC-08）。
   * 1件でもあると通常の書き出しは 422 で止まるので、ここでもボタンを止めて理由を出す。
   */
  const missingAssets = project.missing_assets ?? [];
  const allReady = missingAssets.length === 0;
  /** クリップだけが足りない場合に限り「静止画版の仮プレビュー」で書き出せる */
  const canExportStill = missingAssets.length > 0 && missingAssets.every((m) => m.type === 'clip');
  const isStillPreview = project.final_video_kind === 'still_preview';

  return (
    <div className="space-y-6">
      {processNav}

      {/* シーンサムネイル一覧 */}
      <div>
        <h3 className="text-sm font-semibold text-dark mb-2">
          シーン
          <span className="ml-2 text-xs font-normal text-gray-500">{scenes.length}シーン</span>
        </h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {scenes.map((scene, index) => (
            <div key={scene.id} className="shrink-0 w-24">
              <div className="aspect-[9/16] bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center border border-warm-border">
                {scene.clip.video ? (
                  <video src={scene.clip.video.url} preload="metadata" muted className="w-full h-full object-cover" />
                ) : scene.storyboard.image ? (
                  <img src={scene.storyboard.image.url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Film className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex items-center justify-between mt-1 px-0.5">
                <span className="text-[11px] text-gray-500">{index + 1}</span>
                {scene.storyboard.image ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <span className="text-[11px] text-amber-600">未生成</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 合成アクション */}
      <TaskyCard variant="warm" padding="md">
        {/* 何が足りないのかをサーバーの判定そのままで出す。
            以前は「静止画になります」と警告しつつ書き出せてしまい、
            失敗素材が「完成動画」として出力されていた（UGC-08） */}
        {missingAssets.length > 0 && (
          <TaskyAlert tone="warning" className="mb-3">
            <div className="flex flex-col gap-2">
              <span className="font-medium">完成動画に必要な素材が{missingAssets.length}件足りません。</span>
              <ul className="list-disc pl-5 text-xs space-y-0.5">
                {missingAssets.map((item, index) => (
                  <li key={`${item.type}-${item.scene_id ?? index}`}>{item.label}</li>
                ))}
              </ul>
              <div className="flex items-center gap-2 flex-wrap">
                {missingAssets.some((m) => m.type === 'storyboard') && (
                  <TaskyButton variant="outline" size="sm" onClick={onGoToStoryboard}>
                    ストーリーボードへ
                  </TaskyButton>
                )}
                {missingAssets.some((m) => m.type === 'clip') && (
                  <TaskyButton variant="outline" size="sm" onClick={onGoToMotion}>
                    失敗したシーンを再生成
                  </TaskyButton>
                )}
              </div>
            </div>
          </TaskyAlert>
        )}

        {composeFailed && project.compose_error_message && (
          <TaskyAlert tone="error" className="mb-3">
            書き出しに失敗しました: {project.compose_error_message}
          </TaskyAlert>
        )}

        <TaskyButton
          onClick={() => onCompose(false)}
          disabled={!allReady || isComposing || isRequesting}
          isLoading={isComposing || isRequesting}
        >
          {composeFailed ? <RotateCcw className="w-4 h-4" /> : <Combine className="w-4 h-4" />}
          {isComposing
            ? '書き出し中...（完了すると自動で表示されます）'
            : composeFailed
              ? '再試行する'
              : project.final_video_url
                ? '作り直して書き出す'
                : '動画を書き出す'}
        </TaskyButton>

        {/* 静止画版は「完成動画ではない」と分かる形でだけ選べるようにする */}
        {canExportStill && !isComposing && (
          <div className="mt-3 rounded-lg border border-warm-border bg-white p-3">
            <label className="flex items-start gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={stillConfirmed}
                onChange={(e) => setStillConfirmed(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                クリップ{pendingClips.length}件を待たず、静止画にズームを付けた
                <strong className="font-semibold">仮プレビュー</strong>として書き出す（完成動画ではありません）
              </span>
            </label>
            <TaskyButton
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={!stillConfirmed || isRequesting}
              onClick={() => onCompose(true)}
            >
              <Combine className="w-3.5 h-3.5" />
              静止画版で書き出す
            </TaskyButton>
          </div>
        )}

        {isComposing && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              {stageMessage}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${stagePercent}%` }}
              />
            </div>
          </div>
        )}
      </TaskyCard>

      {/* 完成動画 / 静止画版の仮プレビュー */}
      {project.final_video_url && (
        <div>
          <h3 className="text-sm font-semibold text-dark mb-2 flex items-center gap-1.5">
            {isStillPreview ? (
              <ImageIcon className="w-4 h-4 text-amber-500" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            )}
            {isStillPreview ? '静止画版の仮プレビュー' : '完成動画'}
            {project.composed_at && (
              <span className="text-xs font-normal text-gray-400">
                （{new Date(project.composed_at).toLocaleString('ja-JP')} 作成）
              </span>
            )}
          </h3>
          {isStillPreview && (
            <TaskyAlert tone="warning" className="mb-2">
              クリップが揃っていない状態で書き出したものです。完成動画にするには「⑤ モーション」で
              未生成のクリップを作ってから書き出し直してください。
            </TaskyAlert>
          )}
          {/* 9:16は幅基準だと縦にはみ出すため、高さを max-h-[70vh] で制限し幅は縦横比なりに縮める */}
          <div className="max-w-2xl mx-auto flex flex-col items-center">
            <video
              src={project.final_video_url}
              controls
              className="max-h-[70vh] w-auto max-w-full rounded-xl border border-warm-border bg-black object-contain"
            />
            <a
              href={project.final_video_url}
              download={`${project.title}${isStillPreview ? '_静止画版' : ''}.mp4`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-sm border border-warm-border rounded-lg hover:bg-gray-100"
            >
              <Download className="w-4 h-4" />
              ダウンロード
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportStep;
